import React from 'react';
import type { QuestionType } from './surveyTypes';

interface LikertScaleProps {
  value: number;
  onChange: (value: number) => void;
  type?: QuestionType;
}

export const LikertScale: React.FC<LikertScaleProps> = ({ value, onChange, type = 'standard' }) => {
  const getScaleConfig = () => {
    switch (type) {
      case 'college-activities':
        return {
          options: [
            { value: 4, label: 'Have not decided' },
            { value: 3, label: 'Do not plan to do' },
            { value: 2, label: 'Plan to do' },
            { value: 1, label: 'Done or in progress' }
          ],
          layout: 'horizontal'
        };
      case 'skills-importance':
        return {
          options: [
            { value: 4, label: 'Very little' },
            { value: 3, label: 'Some' },
            { value: 2, label: 'Quite a bit' },
            { value: 1, label: 'Very much' }
          ],
          layout: 'horizontal'
        };
      case 'peer-learning-frequency':
      case 'diversity-discussions':
      case 'help-seeking-frequency':
      case 'participation-frequency':
        return {
          options: [
            { value: 4, label: 'Never' },
            { value: 3, label: 'Sometimes' },
            { value: 2, label: 'Often' },
            { value: 1, label: 'Very often' }
          ],
          layout: 'horizontal'
        };
      case 'work-preference':
        return {
          options: [
            { value: 1, label: 'Work I can do alone' },
            { value: 2, label: 'Work that requires lots of interaction with others' }
          ],
          layout: 'vertical'
        };
      case 'team-preference':
        return {
          options: [
            { value: 1, label: 'Getting things done correctly and efficiently' },
            { value: 2, label: 'Making sure the people involved are engaged and supported' }
          ],
          layout: 'vertical'
        };
      case 'decision-preference':
        return {
          options: [
            { value: 1, label: 'Taking command to make a decision' },
            { value: 2, label: 'Letting others make the final decision' }
          ],
          layout: 'vertical'
        };
      case 'feedback-preference':
        return {
          options: [
            { value: 1, label: 'Motivating the person and letting them know they\'re appreciated' },
            { value: 2, label: 'Being accurate and factual about the person\'s performance' }
          ],
          layout: 'vertical'
        };
      case 'plan-response-preference':
        return {
          options: [
            { value: 1, label: 'Analyze and point out the flaws in the plan' },
            { value: 2, label: 'Think about how I can be helpful in making the plan happen' }
          ],
          layout: 'vertical'
        };
      case 'group-behavior':
        return {
          options: [
            { value: 1, label: 'Likely to speak up' },
            { value: 2, label: 'Likely to stay quiet and listen' }
          ],
          layout: 'vertical'
        };
      case 'time-allocation':
        return {
          options: [
            { value: 1, label: 'Very little' },
            { value: 2, label: 'Some' },
            { value: 3, label: 'About half' },
            { value: 4, label: 'Most' },
            { value: 5, label: 'Almost all' }
          ],
          layout: 'horizontal'
        };
      case 'hours-per-week':
        return {
          options: [
            { value: 1, label: '0' },
            { value: 2, label: '1-5' },
            { value: 3, label: '6-10' },
            { value: 4, label: '11-15' },
            { value: 5, label: '16-20' },
            { value: 6, label: '21-25' },
            { value: 7, label: '26-30' },
            { value: 8, label: 'More than 30' }
          ],
          layout: 'horizontal'
        };
      case 'yes-no':
        return {
          options: [
            { value: 1, label: 'Yes' },
            { value: 2, label: 'No' }
          ],
          layout: 'vertical'
        };
      default:
        return {
          options: [
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
            { value: 6, label: '6' },
            { value: 7, label: '7' }
          ],
          layout: 'circular'
        };
    }
  };

  const config = getScaleConfig();

  if (config.layout === 'vertical') {
    return (
      <div className="space-y-3 mt-4">
        {config.options.map((option) => (
          <label key={option.value} className="flex items-start cursor-pointer group">
            <input
              type="radio"
              name={`scale-${Math.random()}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="mt-1 mr-3 text-blue-600 scale-125 flex-shrink-0"
            />
            <span className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-900 transition-colors">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }

  if (config.layout === 'horizontal') {
    return (
      <div className={`flex mt-4 ${
        config.options.length === 4 
          ? 'justify-between items-center' 
          : 'flex-wrap gap-6'
      }`}>
        {config.options.map((option) => (
          <label 
            key={option.value} 
            className={`flex flex-col items-center cursor-pointer ${
              config.options.length === 4 ? 'flex-1' : ''
            }`}
          >
            <input
              type="radio"
              name={`scale-${Math.random()}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="mb-2 text-blue-600 scale-125"
            />
            <span className={`text-gray-700 text-xs text-center leading-tight ${
              config.options.length === 4 ? 'max-w-none' : 'max-w-32'
            }`}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }

  // Standard circular layout for 1-7 scale
  return (
    <div className="flex justify-between items-center gap-2 mt-4">
      {config.options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`w-12 h-12 rounded-full ${
            value === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300'
          } transition-colors duration-200`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};